import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import { CompetitionService } from '../../service/CompetitionService';
import { translatedMessage } from '../../service/LanguageService';
import DataTableUtils from '../../utilities/DataTableUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAuth from '../../hooks/useAuth';
import CompetitionDataTableUtils from '../../utilities/CompetitionDataTableUtils';
import GeneralUtils from '../../utilities/GeneralUtils';
import ErrorMessageDisplayComponent from '../../components/ErrorMessageDisplayComponent';
import ExportButtonComponent from '../../components/ExportButtonComponent';
import { Dropdown } from 'primereact/dropdown';
import EnumService from '../../service/EnumService';
import { MultiSelect } from 'primereact/multiselect';
import DOMPurify from 'dompurify';
import CompetitionUtils from '../../utilities/CompetitionUtils';
import ResultUtils from '../../utilities/ResultUtils';

const sortOrderHeader = [
    "fullName",
    "result",
    "resultType",
    "requestDetail",
    "responseSent",
    "responseDetail",
    "competition"
]

const tableHeader = [
    translatedMessage("application.candidate"),
    translatedMessage("application.result"),
    translatedMessage("application.result.type"),
    translatedMessage("application.contestation.detail"),
    translatedMessage("application.clarification.response.status"),
    translatedMessage("ContestationType.RESPONSE"),    
    translatedMessage("competition.competition")
]

const resultOptions = [
    { label: translatedMessage('Result.ACCEPTED'), value: 'ACCEPTED' },
    { label: translatedMessage('Result.REJECTED'), value: 'REJECTED' },
    { label: translatedMessage('Result.ABSENT'), value: 'ABSENT' },
    { label: translatedMessage('Result.CANCELED'), value: 'CANCELED' },
]

const CompetitionContestationList = () => {
    const [competition, setCompetition] = useState([]);
    const [contestations, setContestations] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('generic.error.get.data');
    const [resultTypes, setResultTypes] = useState([]);

    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();
    const navigate = useNavigate();

    const competitionService = useMemo(() => new CompetitionService(), []);

    const { competitionIdParam } = useParams();

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: 'application_id',
        sortOrder: 1,
        filters: {
            competitionId: { value: competitionIdParam, matchMode: FilterMatchMode.EQUALS },
            fullName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            requestSent: { value: null, matchMode: FilterMatchMode.EQUALS },
            responseSent: { value: null, matchMode: FilterMatchMode.EQUALS },
            requestDetail: { value: null, matchMode: FilterMatchMode.CONTAINS },
            result: { value: null, matchMode: FilterMatchMode.EQUALS },
            resultType: { value: null, matchMode: FilterMatchMode.IN },
        }
    });

    const propToColumnMap = {
        fullName: "first_name||last_name",
        requestSent: 'request_sent',
        responseSent: 'response_sent',
        requestDetail: 'request_detail'
    };

    useEffect(() => {
        const getCompetition = async () => {
            await competitionService.getCompetition(competitionIdParam).then((response) => {
                setCompetition(response);
                //getContestations()
            });
        };

        const getResultTypes = async () => {
            setResultTypes(await EnumService.getEnumByNameAndParam('ResultType', competitionIdParam));
        };

        getCompetition()
        getResultTypes()
    }, [competitionService, competitionIdParam]);

    useEffect(() => {
        lazyLoadContestations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyParams]);

    const lazyLoadContestations = () => {
        setTableLoading(true);
        loadContestations();
    };

    const setCriteria = () => {
        let criteria = {};
        criteria.sortOrder = lazyParams.sortOrder < 0 ? 'DESC' : 'ASC';
        criteria.sortField = propToColumnMap[lazyParams.sortField] || 'application_result_id';
        criteria.competitionId = competitionIdParam;
        criteria.fullName = lazyParams.filters.fullName.value;
        criteria.requestSent = lazyParams.filters.requestSent.value;
        criteria.result = lazyParams.filters.result.value;
        criteria.resultTypes = lazyParams.filters.resultType.value;

        return criteria
    }

    const loadContestations = async () => {
        let criteria = setCriteria();
        criteria.startRow = lazyParams.first;
        criteria.pageSize = lazyParams.rows;

        if (lazyParams.filters.responseSent.value === '-1') {
            criteria.withoutResponse = true;
        } else {
            criteria.responseSent = lazyParams.filters.responseSent.value;
        }
        criteria.requestDetail = lazyParams.filters.requestDetail.value;

        await competitionService.getContestations(criteria)
            .then((data) => {
                // console.log(data.items);
                setContestations(data.items);
                setTotalRecords(data.totalCount);
                setTableLoading(false);
                setIsLoading(false);
            })
            .catch((error) => {
                setError(true);
                setErrorMessage(error);
                setIsLoading(false);
                // toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
            });
    };

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    const viewContestation = (rowData) => {
        navigate(`/competition-evaluation/${competition.id}/application-contestation/${rowData.applicationId}/result/${rowData.applicationResultId}`);
    };

    const onPage = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.first = event.first;
        _lazyParams.page = event.page;
        _lazyParams.rows = event.rows;
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.sortField = event.sortField;
        _lazyParams.sortOrder = event.sortOrder;
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyParams(event);
    };

    const leftToolbarTemplate = () => {
        return <></>;
    };

    const handleExport = async () => {
        let criteria = setCriteria();
        return new Promise((resolve, reject) => {
            competitionService.getContestations(criteria)
                .then((data) => {
                    let exportData = data.items.map(item => ({ ...item }));
                    exportData.map(item => {
                        let exportItem = item
                        exportItem.result = item.result ? translatedMessage('Result.' + item.result) : ""
                        exportItem.resultType = item.resultType ? ResultUtils.getResultTypeByCompetitionAndResultType(competition, item.resultType) : ""
                        exportItem.responseSent = item.responseSent === true
                            ? translatedMessage('application.response.sent')
                            : (item.responseSent === false
                                ? translatedMessage('application.response.not.sent')
                                : translatedMessage('application.response.not.filled')
                            )
                        exportItem.requestDetail = item.requestDetail ? DOMPurify.sanitize(item.requestDetail, { ALLOWED_TAGS: [] }) : ""
                        exportItem.responseDetail = item.responseDetail ? DOMPurify.sanitize(item.responseDetail, { ALLOWED_TAGS: [] }) : ""
                        exportItem.competition = competition.name                        
                        delete exportItem.applicationId
                        delete exportItem.competitionId
                        delete exportItem.applicationResultId
                        delete exportItem.applicationStatus
                        delete exportItem.requestSent
                        delete exportItem.firstName
                        delete exportItem.lastName
                        delete exportItem.requestSent
                        delete exportItem.requestType
                        delete exportItem.responseType
                        delete exportItem.userId

                        return exportItem;
                    })

                    resolve(exportData)
                })
                .catch((error) => reject(error));
        });
    }

    const rightToolbarTemplate = () => {
        return (
            <ExportButtonComponent
                getExportData={handleExport}
                header={tableHeader}
                sortOrderHeader={sortOrderHeader}
                fileName={GeneralUtils.replaceSpaces(translatedMessage('application.contestation') + "_" + competition?.name)}
            />
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-primary m-1"
                    onClick={() => viewContestation(rowData)}
                    visible={hasPermission('EVALUATION_VIEW')}
                    tooltip={translatedMessage('application.view')}
                    tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                />
            </div>
        );
    };

    // const contestationDetailBodyTemplate = (rowData) => {
    //     let cleanContent = GeneralUtils.getHTMLSubstringWithEllipsis(rowData.requestDetail, 200);
    //     return <span dangerouslySetInnerHTML={{ __html: cleanContent }} />;
    // };

    const resultTemplate = (_result) => {
        return <>{<span className={`status status-result-${_result.toString().toLowerCase()}`}>{translatedMessage('Result.' + _result)}</span>}</>;
    };

    const resultFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value}
                options={resultOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                className="p-column-filter"
                itemTemplate={(_result) => {
                    return <span className={`status status-result-${_result.value.toString().toLowerCase()}`}>{translatedMessage('Result.' + _result.value)}</span>;
                }}
            />
        );
    };

    const resultTypeFilterTemplate = (options) => {
        return <MultiSelect
            value={options.value}
            options={resultTypes}
            onChange={(e) => options.filterApplyCallback(e.value)}
            placeholder={translatedMessage('generic.type')}
            className="p-column-filter"
            maxSelectedLabels={1}
            showSelectAll={false}
            selectedItemsLabel={"{0} " + translatedMessage("multiselect.selectedItems")}
            itemTemplate={(e) => ResultUtils.resultTypeTemplateByResultType(competition, e.value)}
        />
    }

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else if (error) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ErrorMessageDisplayComponent message={errorMessage} />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <h3 className="mb-1">{competition?.name}</h3>
                            <div className="mb-3">
                                <Button className="pcn-button-slim p-button-text" onClick={() => navigate(`/competition-evaluation`)}>
                                    <div className="flex align-items-center">
                                        <FontAwesomeIcon icon="fa-solid fa-arrow-left" className="mr-1 " />
                                        <span>{translatedMessage('generic.backToList')}</span>
                                    </div>
                                </Button>
                            </div>

                            <div className="mb-4">
                                <h5 className="mb-1 font-weight-normal">{translatedMessage('application.contestation')}</h5>
                                <div className="flex align-items-center">
                                    <div className="font-bold mr-1">{translatedMessage('competition.status') + ':'}</div>
                                    <div className={`inline-block status status-competition-${competition?.status?.toString().toLowerCase()}`}>
                                        {CompetitionUtils.getCompetitionStatus(competition)}
                                    </div>
                                </div>
                            </div>
                            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                            <DataTable
                                ref={dt}
                                value={contestations}
                                dataKey="id"
                                loading={tableLoading}
                                lazy
                                paginator
                                totalRecords={totalRecords}
                                first={lazyParams.first}
                                rows={lazyParams.rows}
                                sortField={lazyParams.sortField}
                                sortOrder={lazyParams.sortOrder}
                                onPage={onPage}
                                onSort={onSort}
                                onFilter={onFilter}
                                rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                                className="datatable-responsive pcn-datatable"
                                responsiveLayout="stack"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                                emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                filterDisplay="row"
                                filters={lazyParams.filters}
                            >
                                <Column                                     
                                    field="fullName" 
                                    header={translatedMessage('generic.name')} 
                                    sortable 
                                    headerStyle={{ width: '30%', minWidth: '10rem' }} 
                                    filter 
                                    showFilterMenu={false} />
                                <Column
                                    field="responseSent"
                                    header={translatedMessage('application.clarification.response.status')}
                                    sortable
                                    headerStyle={{ width: '15%', minWidth: '10rem' }}
                                    body={(e) => CompetitionDataTableUtils.contestationResponseSentBodyTemplate(e.responseSent)}
                                    filter
                                    filterElement={(e) => CompetitionDataTableUtils.contestationResponseSentFilterTemplate(e)}
                                    showFilterMenu={false}
                                />
                                <Column
                                    field="result"
                                    header={translatedMessage('application.result')}
                                    sortable
                                    headerStyle={{ width: '15%', minWidth: '10rem' }}
                                    body={(e) => resultTemplate(e.result)}
                                    filter
                                    filterElement={resultFilterTemplate}
                                    showFilterMenu={false}
                                />
                                <Column
                                    field="resultType"
                                    header={translatedMessage('application.result.type')}
                                    sortable
                                    headerStyle={{ width: '25%', minWidth: '10rem' }}
                                    body={(e) => ResultUtils.resultTypeTemplateByResultType(competition, e.resultType)}
                                    filter
                                    filterElement={resultTypeFilterTemplate}
                                    showFilterMenu={false}
                                />
                                {/* <Column
                                    field="requestDetail"
                                    header={translatedMessage('application.contestation.detail')}
                                    sortable
                                    headerStyle={{ width: '45%', minWidth: '10rem' }}
                                    body={contestationDetailBodyTemplate}
                                    filter
                                    showFilterMenu={false}
                                /> */}

                                <Column body={actionBodyTemplate}></Column>
                            </DataTable>
                        </div>
                    </div>
                </div>
            </>
        );
    }
};

export default CompetitionContestationList;
